// src/components/SettingsModal.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../../contexts/SettingsContext';
import useAuth from '../../../hooks/useAuth';
import { FaMoon, FaSun } from "react-icons/fa";
import styles from './SettingsModal.module.css';
import {
    FaTimes,
    FaGlobe,
    FaSave,
    FaPalette,
    FaCheckCircle,
    FaUser,
    FaEnvelope,
    FaBriefcase,
    FaIdBadge,
    FaUserTag
} from 'react-icons/fa';
import i18n from '../../../i18n/i18n';

const SettingsModal = ({ isOpen, onClose }) => {
    const { t, i18n: i18nInstance } = useTranslation();
    const { settings, updateSettings } = useSettings();
    const { currentUser } = useAuth();
    const [localSettings, setLocalSettings] = useState(settings);
    const [saved, setSaved] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Handle escape key - moved BEFORE conditional return
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    // Reset local settings when modal opens
    useEffect(() => {
        if (isOpen) {
            setLocalSettings(settings);
            setSaved(false);
            setIsSaving(false);
        }
    }, [isOpen, settings]);

    // If modal is not open, return null after all hooks
    if (!isOpen) return null;

    const handleChange = (key, value) => {
        setLocalSettings(prev => ({
            ...prev,
            [key]: value
        }));
        setSaved(false);
    };

    const handleSave = () => {
        setIsSaving(true);

        // FIX: Apply language change immediately
        if (localSettings.language && localSettings.language !== settings.language) {
            i18nInstance.changeLanguage(localSettings.language);
        }

        // Simulate save delay for better UX
        setTimeout(() => {
            updateSettings(localSettings);
            setSaved(true);
            setIsSaving(false);
            // Auto-hide saved message after 3 seconds
            setTimeout(() => setSaved(false), 3000);
        }, 500);
    };

    // Get role label for display
    const getRoleLabel = (roleValue) => {
        const roleMap = {
            'Administracao': 'Administração',
            'Comercial': 'Comercial',
            'Diretoria (Admin)': 'Diretoria (Admin)',
            'Laboratorio': 'Laboratório',
            'Logistica': 'Logística',
            'Marketing': 'Marketing',
            'TI': 'TI',
            'Inspetor': 'Inspetor',
            'Operacional': 'Operacional'
        };
        return roleMap[roleValue] || roleValue || 'N/A';
    };

    const languages = [
        { value: 'pt-BR', label: t('settings.languageOptions.portuguese'), flag: '🇧🇷' },
        { value: 'en-US', label: t('settings.languageOptions.english'), flag: '🇺🇸' },
        { value: 'ar', label: t('settings.languageOptions.arabic'), flag: '🇸🇦' },
    ];

    const themes = [
        { value: 'light', label: t('settings.themeOptions.light'), icon: <FaSun /> },
        { value: 'dark', label: t('settings.themeOptions.dark'), icon: <FaMoon /> },
    ];

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContainer} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <FaGlobe className={styles.modalIcon} />
                        {t('settings.title')}
                    </h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {saved && (
                        <div className={styles.successMessage}>
                            <FaCheckCircle className={styles.successIcon} />
                            {t('settings.saved')}
                        </div>
                    )}

                    {/* User Profile Section - Read Only */}
                    <div className={styles.settingGroup}>
                        <label className={styles.settingLabel}>
                            <FaUser className={styles.labelIcon} />
                            {t('settings.profile.title')}
                        </label>
                        <div className={styles.profileCard}>
                            <div className={styles.profileAvatar}>
                                <span className={styles.avatarLetter}>
                                    {currentUser?.nome?.charAt(0)?.toUpperCase() || currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                            </div>
                            <div className={styles.profileInfo}>
                                <div className={styles.profileRow}>
                                    <FaIdBadge className={styles.profileIcon} />
                                    <span className={styles.profileLabel}>{t('settings.profile.name')}:</span>
                                    <span className={styles.profileValue}>{currentUser?.nome || 'N/A'}</span>
                                </div>
                                <div className={styles.profileRow}>
                                    <FaEnvelope className={styles.profileIcon} />
                                    <span className={styles.profileLabel}>{t('settings.profile.email')}:</span>
                                    <span className={styles.profileValue}>{currentUser?.email || 'N/A'}</span>
                                </div>
                                <div className={styles.profileRow}>
                                    <FaUserTag className={styles.profileIcon} />
                                    <span className={styles.profileLabel}>{t('settings.profile.role')}:</span>
                                    <span className={styles.profileValue}>
                                        <span className={styles.roleBadge}>
                                            {getRoleLabel(currentUser?.role)}
                                        </span>
                                    </span>
                                </div>
                                {currentUser?.cargoNome && (
                                    <div className={styles.profileRow}>
                                        <FaBriefcase className={styles.profileIcon} />
                                        <span className={styles.profileLabel}>{t('settings.profile.position')}:</span>
                                        <span className={styles.profileValue}>{currentUser.cargoNome}</span>
                                    </div>
                                )}
                                <div className={styles.profileDivider} />
                                <div className={styles.profileRow}>
                                    <span className={styles.profileStatus}>
                                        <span className={styles.statusDot}></span>
                                        {t('settings.profile.active')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Language Settings */}
                    <div className={styles.settingGroup}>
                        <label className={styles.settingLabel}>
                            <FaGlobe className={styles.labelIcon} />
                            {t('settings.language')}
                        </label>
                        <div className={styles.languageOptions}>
                            {languages.map(lang => (
                                <button
                                    key={lang.value}
                                    className={`${styles.languageOption} ${localSettings.language === lang.value ? styles.active : ''
                                        }`}
                                    onClick={() => handleChange('language', lang.value)}
                                >
                                    <span className={styles.flagIcon}>{lang.flag}</span>
                                    <span className={styles.languageName}>{lang.label}</span>
                                    {localSettings.language === lang.value && (
                                        <span className={styles.checkMark}>✓</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Theme Settings */}
                    <div className={styles.settingGroup}>
                        <label className={styles.settingLabel}>
                            <FaPalette className={styles.labelIcon} />
                            {t('settings.theme')}
                        </label>
                        <div className={styles.themeOptions}>
                            {themes.map(theme => (
                                <button
                                    key={theme.value}
                                    className={`${styles.themeOption} ${localSettings.theme === theme.value ? styles.active : ''
                                        }`}
                                    onClick={() => handleChange('theme', theme.value)}
                                >
                                    <span className={styles.themeIcon}>{theme.icon}</span>
                                    <span className={styles.themeName}>{theme.label}</span>
                                    {localSettings.theme === theme.value && (
                                        <span className={styles.checkMark}>✓</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.cancelButton} onClick={onClose}>
                        {t('settings.close')}
                    </button>
                    <button
                        className={styles.saveButton}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        <FaSave className={styles.saveIcon} />
                        {isSaving ? t('common.loading') : t('settings.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SettingsModal;